import { getCellExternalId } from '../../table/utils/cellIdUtils';

/**
 * Serializes the selected cells to a TSV grid (tab-separated columns,
 * newline-separated rows) — the format spreadsheet apps expect on paste.
 *
 * Text is resolved by calling `getCellCopyValue` when provided, falling back to
 * DOM `textContent` of the cell element. Cells not found in the live DOM are
 * looked up in `stagingContainer` (a hidden render area used for virtualized
 * cells), and are left as empty strings only if absent from both.
 *
 * The output is always the full bounding box of the selection: non-selected
 * cells inside the bounding box are serialized as empty strings.
 */
export const serializeSelectionToTsv = (coords, columns, rows, getRowId, getCellCopyValue, stagingContainer) => {
  if (coords.length === 0) return '';
  const sorted = [...coords].sort((a, b) => a.rowIndex - b.rowIndex || a.columnIndex - b.columnIndex);
  const rowGroups = new Map();
  sorted.forEach(({
    rowIndex,
    columnIndex
  }) => {
    var _document$getElementB, _element$textContent$, _element$textContent;
    const row = rows[rowIndex];
    const column = columns[columnIndex];
    if (row === undefined || column === undefined) return;
    const externalId = getCellExternalId(column.columnNamespace, column.columnName, getRowId(row));
    const element = (_document$getElementB = document.getElementById(externalId)) !== null && _document$getElementB !== void 0 ? _document$getElementB : stagingContainer ? stagingContainer.querySelector(`[data-fdt-row-index="${rowIndex}"][data-column-index="${columnIndex}"]`) : null;
    if (!element) return;
    const raw = getCellCopyValue ? getCellCopyValue({
      columnName: column.columnName,
      columnNamespace: column.columnNamespace,
      row,
      element
    }) : (_element$textContent$ = (_element$textContent = element.textContent) === null || _element$textContent === void 0 ? void 0 : _element$textContent.trim()) !== null && _element$textContent$ !== void 0 ? _element$textContent$ : '';
    // Prefix with a single quote to prevent spreadsheet apps (Excel, Google
    // Sheets) from interpreting values starting with =, +, -, or @ as formulas.
    // The quote signals "treat this as a string literal" on paste.
    const text = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
    // RFC 4180 quoting: wrap fields containing structural characters (\r, \n,
    // \t, ") in double quotes and escape internal double quotes as "".
    // Spreadsheet apps treat quoted fields as a single cell even when the value
    // spans multiple lines or contains tabs.
    const sanitized = /[\r\n\t"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    if (!rowGroups.has(rowIndex)) {
      rowGroups.set(rowIndex, []);
    }
    rowGroups.get(rowIndex).push({
      columnIndex,
      text: sanitized
    });
  });
  if (rowGroups.size === 0) return '';
  const minRow = sorted[0].rowIndex;
  const maxRow = sorted[sorted.length - 1].rowIndex;
  const minCol = sorted.reduce((min, c) => Math.min(min, c.columnIndex), sorted[0].columnIndex);
  const maxCol = sorted.reduce((max, c) => Math.max(max, c.columnIndex), sorted[0].columnIndex);
  const lines = [];
  for (let row = minRow; row <= maxRow; row++) {
    const rowCells = rowGroups.get(row);
    const cellMap = new Map();
    if (rowCells) {
      rowCells.forEach(c => cellMap.set(c.columnIndex, c.text));
    }
    const cells = [];
    for (let col = minCol; col <= maxCol; col++) {
      var _cellMap$get;
      cells.push((_cellMap$get = cellMap.get(col)) !== null && _cellMap$get !== void 0 ? _cellMap$get : '');
    }
    lines.push(cells.join('\t'));
  }
  return lines.join('\n');
};